/**
 * 4-tier source authority ranking.
 *
 * Tier 1 (Wire):       Global wire services — Reuters, AP, AFP, Bloomberg, UPI.
 * Tier 2 (Major):      Major international outlets — BBC, Guardian, FT, NPR, etc.
 * Tier 3 (Specialist): Domain experts, think tanks, IGOs — ReliefWeb, UN, GDACS, WHO.
 * Tier 4 (Aggregator): Tech blogs, aggregators, niche outlets.
 *
 * Matching logic checks feed ID, display name, and domain substrings
 * so a single feed can be classified without touching every FeedDef.
 */

export type AuthorityTier = 1 | 2 | 3 | 4;

export interface AuthorityInfo {
  tier: AuthorityTier;
  label: string;
}

const TIER_LABELS: Record<AuthorityTier, string> = {
  1: 'Wire Service',
  2: 'Major Outlet',
  3: 'Specialist',
  4: 'Aggregator',
};

/**
 * Tier 1 — Wire services.
 * Global newswires with direct editorial staff in every major bureau.
 * NOTE: patterns are matched against a normalized string where hyphens
 * and underscores become spaces. Keep patterns lowercase, space-separated.
 */
const TIER_1_PATTERNS: string[] = [
  // Global wire services
  'reuters', 'ap news', 'apnews', 'associated press', 'afp.com',
  'agence france', 'bloomberg', 'upi', 'xinhua',
  'kyodo', 'efe', 'dpa', 'pti',
  'ansa', 'anadolu', 'yonhap', 'itar', 'interfax',
  'bernama', 'irna',
  'reuters.com', 'apnews.com', 'bloomberg.com',
];

/**
 * Tier 2 — Major international news outlets.
 * Large newsrooms with international correspondents.
 */
const TIER_2_PATTERNS: string[] = [
  // Major broadcasters
  'bbc', 'cnn', 'nhk', 'abc news', 'euronews', 'sky news',
  'pbs', 'npr', 'cbc', 'abc australia',
  // Major print / digital
  'guardian', 'financial times', 'al jazeera', 'aljazeera',
  'france24', 'france 24', 'dw news', 'dw.com',
  'washington post', 'wapo', 'ny times', 'nyt', 'nytimes',
  'wall street journal', 'wsj', 'economist', 'le monde',
  'der spiegel', 'south china morning post', 'scmp',
  'times of india', 'cnbc', 'politico', 'nikkei', 'haaretz',
  'ndtv', 'channel news asia', 'cna.asia', 'straits times',
  'abc.net.au', 'daily telegraph', 'irish times',
  // Business press
  'marketwatch', 'investing.com', 'cnbc.com', 'ft.com',
  // Major regional outlets
  'kyiv independent', 'times of israel', 'middle east eye',
  'al monitor', 'daily maverick', 'the hindu',
  'japan times', 'korea herald', 'taipei times',
  'globe and mail', 'sydney morning herald',
  'el pais', 'corriere della sera', 'aftenposten',
  'nrc handelsblad', 'sueddeutsche', 'die zeit',
  'punch nigeria', 'fiji times', 'the africa report',
  // State-aligned outlets (treat as major for classification, bias flagged elsewhere)
  'rt.com', 'cgtn', 'cgtn.com', 'press tv', 'presstv',
  'sputniknews', 'global times', 'globaltimes',
  'tass', 'ria novosti', 'kcna',
  // Additional major outlets
  'asahi', 'asahi shimbun', 'mainichi', 'yomiuri',
  'handelsblatt', 'les echos', 'la repubblica',
  'rest of world',
  'bbc.co.uk',
];

/**
 * Tier 3 — Specialist and institutional sources.
 * Domain experts, think tanks, intergovernmental organizations.
 */
const TIER_3_PATTERNS: string[] = [
  // Intergovernmental organizations
  'reliefweb', 'un news', 'un.org', 'who news', 'who.int',
  'world health', 'gdacs', 'world bank', 'imf', 'wto',
  'iaea', 'icrc', 'ecdc', 'ecb', 'bankofengland', 'boe.co.uk',
  'fed reserve', 'fed speech', 'fed-speech',
  'federalreserve', 'interpol', 'nato', 'osce',

  // Government agencies
  'nasa', 'noaa', 'usgs', 'cisa', 'cdc', 'faa', 'easa',
  'eurocontrol', 'nws', 'state.gov', 'gov.uk',
  'smartraveller', 'safetravel', 'ofac', 'treasury.gov',

  // Think tanks & research
  'iiss', 'csis', 'rand', 'chatham house', 'sipri',
  'crisis group', 'brookings', 'carnegie', 'atlantic council',
  'wilson center', 'foreign affairs', 'foreign policy',
  'war on the rocks', 'piie', 'council on foreign relations',
  'cfr.org', 'rusi', 'lowy institute',

  // Human rights & NGOs
  'amnesty', 'hrw', 'human rights watch',
  'transparency international', 'reporters without borders',
  'freedom house', 'open society',

  // Defense & military specialist
  'janes', 'janes.com', 'janes defence', 'defense news', 'breaking defense',
  'defense one', 'uk defence journal', 'naval news',
  'the aviationist', 'stratfor',

  // Conflict & OSINT
  'acled', 'bellingcat', 'liveuamap', 'euromaidan press',
  'ucdp', 'armed conflict location',

  // Science & academic
  'sciencedaily', 'phys.org', 'phys org', 'nature.com',
  'the lancet', 'stat news', 'new scientist',
  'carbon brief', 'bulletin of the atomic', 'arms control',
  'nti.org', 'nuclear threat',

  // Mongabay (environmental specialist)
  'mongabay',

  // Maritime specialist
  'gcaptain', 'splash247', 'maritime executive', 'lloyds list',
  'tradewinds',

  // Energy specialist
  'oilprice', 'rigzone', 'world nuclear news',
  'mining.com',

  // Space specialist
  'spacenews', 'nasaspaceflight', 'space.com',
  'space intel',

  // Cyber specialist
  'krebs on security', 'krebsonsecurity',
  'bleepingcomputer', 'bleeping computer',
  'dark reading', 'darkreading', 'the record',
  'the hacker news', 'security week',
  'vx underground',

  // Crypto specialist
  'coindesk', 'cointelegraph', 'the block',

  // Data/transparency
  'cloudflare', 'cloudflare radar',

  // OSINT & investigative
  'the war zone', 'thedrive.com/the-war-zone',
  'usni', 'usni news', 'cyberscoop',
  'lawfare', 'lawfaremedia', 'just security',
  'grey dynamics', 'insight crime',
  'the intercept', 'intercept',

  // Tech specialist (domain-specific, not general major outlets)
  'techcrunch', 'ars technica', 'arstechnica', 'the verge',
  'wired', 'mit technology review',

  // AI/ML research
  'huggingface', 'hugging face', 'deeplearning.ai',
  'openai.com/blog', 'import ai',
  'semianalysis', 'semiconductor engineering',
  'ee times', 'eetimes', 'ieee spectrum',
  'center ai safety', 'quantum insider',

  // Humanitarian / development
  'devex', 'new humanitarian', 'thenewhumanitarian',
  'msf', 'médecins sans frontières', 'doctors without borders',
  'oxfam', 'migration policy', 'iom.int',
  'fao.org', 'wfp.org', 'fews.net',
  'circle of blue', 'mixed migration',

  // Regional specialist
  'eurasianet', 'meduza', 'moscow times',
  'balkan insight', 'baltic times',
  'mercopress', 'buenos aires times',
  'nation africa', 'africa confidential',
  'asia times', 'east asia forum',
  'bangkok post', 'korea herald',
  'rnz pacific',
  'euobserver', 'bne intellinews', 'third pole',
  'jam news',

  // Economics & finance specialist
  'project syndicate', 'finextra', 'pymnts',
  'kitco', 'agri investor',
  'oecd', 'unctad', 'bis.org',
  'the information', 'american banker',
  'sanctions expert',

  // Nonproliferation & arms control
  'vertic', 'nonproliferation',
  'as-coa', 'americas society',

  // Algorithmic & tech policy
  'algorithmwatch',

  // Transport / aviation specialist
  'aviation week', 'flightglobal', 'simple flying',
  'aviation safety network', 'freightwaves',
  'seatrade', 'hellenic shipping',
];

// Everything else falls to Tier 4.

/**
 * Normalize a source string for pattern matching.
 * Converts hyphens and underscores to spaces, collapses whitespace,
 * and lowercases. This ensures feed IDs like "un-news" match patterns
 * like "un news", and display names like "Al Jazeera" match too.
 */
function normalizeForMatch(source: string): string {
  return source.toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Resolve the authority tier for a source identifier.
 * Accepts feed IDs (e.g. "reuters-world"), display names ("Reuters"),
 * or domain fragments ("reuters.com").
 *
 * Matching is case-insensitive substring search against the pattern lists.
 * Hyphens and underscores are normalized to spaces before matching.
 */
export function getAuthorityTier(source: string): AuthorityTier {
  const normalized = normalizeForMatch(source);

  for (const pat of TIER_1_PATTERNS) {
    if (normalized.includes(pat)) return 1;
  }
  for (const pat of TIER_2_PATTERNS) {
    if (normalized.includes(pat)) return 2;
  }
  for (const pat of TIER_3_PATTERNS) {
    if (normalized.includes(pat)) return 3;
  }
  return 4;
}

/**
 * Get full authority info (tier + human-readable label).
 */
export function getAuthorityInfo(source: string): AuthorityInfo {
  const tier = getAuthorityTier(source);
  return { tier, label: TIER_LABELS[tier] };
}

/**
 * Get the display label for a tier number.
 */
export function getTierLabel(tier: AuthorityTier): string {
  return TIER_LABELS[tier];
}
